import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { ICheckUserId } from '@vacationtracker/shared/types/company'
import { EmailNotValid, EmailPlatformUserInactive, UserNotFound } from '@vacationtracker/shared/errors/users'

import { ConnectPlatform, EmailOutcome, IAuthInfoResponse, PlatformOutcome, SignupBanner } from './types'

const CONNECT_PLATFORMS: ConnectPlatform[] = [PlatformEnum.Slack, PlatformEnum.Microsoft, PlatformEnum.Google]

export const isConnectPlatform = (value: unknown): value is ConnectPlatform => typeof value === 'string' && CONNECT_PLATFORMS.includes(value as ConnectPlatform)

export const authInfoToOutcome = (response: IAuthInfoResponse | null | undefined): EmailOutcome => {
  if (!response?.platform) return { kind: 'unknownError' }

  // `platform` names one account out of however many the address reaches, and which one it
  // names is up to the index. Anything with a choice in it goes to the page that can offer
  // the choice, so the answer does not depend on the order the rows came back in.
  const reachesSeveralAccounts = (response.availablePlatforms?.length ?? 0) > 1

  if (response.platform === PlatformEnum.Email && !reachesSeveralAccounts) {
    return { kind: 'alreadySignedUp' }
  }

  return { kind: 'existsOnOtherPlatform', platform: response.platform, authInfo: response }
}

const readDomainPlatform = (data: unknown): string | undefined => {
  if (typeof data !== 'object' || data === null) return undefined
  const domainPlatform = (data as { domainPlatform?: unknown }).domainPlatform
  return typeof domainPlatform === 'string' && domainPlatform ? domainPlatform : undefined
}

/**
 * Matched on the error code the API returns rather than on the axios message. The old page
 * tested `error.message.includes('404')`, so any 404 body at all walked on to personal-info.
 */
export const authInfoErrorToOutcome = (error): EmailOutcome => {
  const code = error?.response?.data?.code

  if (code === UserNotFound.code) {
    const domainPlatform = readDomainPlatform(error?.response?.data?.data)
    return domainPlatform ? { kind: 'domainSuggestsPlatform', domainPlatform } : { kind: 'newAccount' }
  }

  if (code === EmailPlatformUserInactive.code) return { kind: 'inactive' }
  if (code === EmailNotValid.code) return { kind: 'invalidEmail' }

  return { kind: 'unknownError' }
}

export const checkUserIdToOutcome = (userData: ICheckUserId | null | undefined): PlatformOutcome => {
  if (!userData) return { kind: 'proceed' }

  // Microsoft and Google report a stopped account as an unknown one, and only they know which
  // company the attempt landed in. Signing up again cannot fix it and the company is not
  // "already taken" either: the person is a member whose account was turned off.
  if (userData.userStatus && userData.userStatus !== 'ACTIVE') {
    return { kind: 'accountInactive', email: userData.mail || '' }
  }

  if (userData.type === 'USER_NOT_FOUND') {
    return userData.subscriptionStatus === 'canceled' ? { kind: 'subscriptionExpired' } : { kind: 'companyExists' }
  }

  if (userData.type === 'COMPANY_NOT_FOUND') {
    if (userData.existsOnAnotherPlatform && userData.existingOrganizationName) {
      return {
        kind: 'newCompanyOnOtherPlatform',
        platform: userData.existsOnAnotherPlatform,
        organizationName: userData.existingOrganizationName,
      }
    }
    return { kind: 'newCompany' }
  }

  // No type at all: an active account, so a token sign-in is tried on it. The page only acts on
  // this outcome once that sign-in was refused.
  return { kind: 'proceed' }
}

export const bannerFromEmailOutcome = (params: { outcome: EmailOutcome; email: string }): SignupBanner => {
  const { outcome, email } = params

  switch (outcome.kind) {
    case 'alreadySignedUp':
      return { kind: 'alreadySignedUp', email }
    case 'invalidEmail':
      return { kind: 'invalidEmail' }
    case 'unknownError':
      return { kind: 'signupFailed' }
    default:
      // The rest open a modal or leave the page, so the banner slot stays empty behind them.
      return { kind: 'none' }
  }
}

export const bannerFromPlatformOutcome = (outcome: PlatformOutcome): SignupBanner => {
  if (outcome.kind === 'subscriptionExpired') return { kind: 'subscriptionExpired' }
  if (outcome.kind === 'newCompanyOnOtherPlatform') {
    return { kind: 'orgOnOtherPlatform', platform: outcome.platform, organizationName: outcome.organizationName }
  }
  return { kind: 'none' }
}

/**
 * Whether the flow leaves this page for the next signup step. The busy flag stays on when it
 * does, so the form is not briefly re-enabled underneath a navigation already in flight.
 */
export const emailOutcomeNavigatesAway = (outcome: EmailOutcome): boolean =>
  outcome.kind === 'newAccount' || outcome.kind === 'existsOnOtherPlatform' || outcome.kind === 'inactive'

export const platformOutcomeNavigatesAway = (outcome: PlatformOutcome): boolean => outcome.kind === 'newCompany' || outcome.kind === 'proceed' || outcome.kind === 'accountInactive'
